import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import HelpModal from '../components/HelpModal';
import { helpGuides } from '../utils/helpGuides';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface Freebie {
  id: number;
  title: string;
  description: string;
  category: string;
  file_url: string;
  file_name: string;
  file_type: string;
  file_size: number;
  thumbnail_url?: string;
  required_tier: string;
  download_count: number;
  is_active: boolean;
}

const Freebies: React.FC = () => {
  const navigate = useNavigate();
  const [freebies, setFreebies] = useState<Freebie[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadCount, setDownloadCount] = useState(0);
  const [userTier, setUserTier] = useState('free');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [downloading, setDownloading] = useState<number | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [previewFreebie, setPreviewFreebie] = useState<Freebie | null>(null);

  const categories = [
    { value: 'all', label: '📚 All Freebies', icon: '📚' },
    { value: 'templates', label: '📄 Templates & Planners', icon: '📄' },
    { value: 'guides', label: '📖 Guides & Checklists', icon: '📖' },
    { value: 'swipe_files', label: '💌 Swipe Files', icon: '💌' },
    { value: 'worksheets', label: '📝 Worksheets & Workbooks', icon: '📝' },
    { value: 'tools', label: '🔧 Automation & Tools', icon: '🔧' }
  ];

  useEffect(() => {
    fetchFreebies();
  }, []);

  const fetchFreebies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/freebies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFreebies(response.data.freebies);
      setDownloadCount(response.data.downloadCount);
      setUserTier(response.data.userTier);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching freebies:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
      setLoading(false);
    }
  };

  const handleDownload = async (freebieId: number, freebieTitle: string) => {
    setDownloading(freebieId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/freebies/${freebieId}/download`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Open file in new tab or trigger download
      if (response.data.fileUrl) {
        window.open(response.data.fileUrl, '_blank');
      }

      // Update download count
      setDownloadCount(downloadCount + 1);
      alert(`✅ ${freebieTitle} downloaded successfully!`);
    } catch (error: any) {
      console.error('Download error:', error);
      if (error.response?.data?.requiresUpgrade) {
        if (window.confirm(error.response.data.error + '\n\nGo to pricing page?')) {
          navigate('/pricing');
        }
      } else {
        alert(error.response?.data?.error || 'Failed to download freebie');
      }
    } finally {
      setDownloading(null);
    }
  };

  const filteredFreebies = selectedCategory === 'all'
    ? freebies
    : freebies.filter(f => f.category === selectedCategory);

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-gray-100 text-gray-800';
      case 'basic': return 'bg-blue-100 text-blue-800';
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'lifetime': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading freebies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">🎁 Freebie Vault</h1>
              <p className="mt-1 text-sm text-gray-500">
                Download templates, guides, and resources to grow your course business
              </p>
            </div>
            <div className="flex gap-3 items-center">
              {userTier === 'basic' && (
                <div className="text-sm">
                  <span className="text-gray-600">Downloads:</span>
                  <span className="ml-2 font-semibold text-indigo-600">{downloadCount} / 5</span>
                  {downloadCount >= 5 && (
                    <span className="ml-2 text-red-600 text-xs">(Limit reached)</span>
                  )}
                </div>
              )}
              {['premium', 'lifetime'].includes(userTier) && (
                <div className="text-sm">
                  <span className="text-gray-600">Downloads:</span>
                  <span className="ml-2 font-semibold text-green-600">{downloadCount} (Unlimited)</span>
                </div>
              )}
              <button
                onClick={() => setShowHelpModal(true)}
                className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200 flex items-center gap-2 font-medium"
                title="How to use the Freebie Vault"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Help
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Filter */}
        <div className="mb-8 flex gap-3 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedCategory === cat.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {cat.icon} {cat.label.replace(cat.icon + ' ', '')}
            </button>
          ))}
        </div>

        {/* Freebies Grid */}
        {filteredFreebies.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No freebies available in this category yet.</p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for new resources!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFreebies.map(freebie => (
              <div key={freebie.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden">
                {/* Thumbnail or Icon */}
                <div className="h-40 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  {freebie.thumbnail_url ? (
                    <img src={freebie.thumbnail_url} alt={freebie.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-6xl">
                      {freebie.category === 'templates' && '📄'}
                      {freebie.category === 'guides' && '📖'}
                      {freebie.category === 'swipe_files' && '💌'}
                      {freebie.category === 'worksheets' && '📝'}
                      {freebie.category === 'tools' && '🔧'}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 text-lg flex-1">{freebie.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap ml-2 ${getTierBadgeColor(freebie.required_tier)}`}>
                      {freebie.required_tier.toUpperCase()}
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{freebie.description}</p>

                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      📎 {freebie.file_type?.toUpperCase() || 'FILE'}
                    </span>
                    <span>{formatFileSize(freebie.file_size)}</span>
                    <span>⬇️ {freebie.download_count}</span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewFreebie(freebie)}
                      className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition"
                    >
                      👁️ Preview
                    </button>
                    <button
                      onClick={() => handleDownload(freebie.id, freebie.title)}
                      disabled={downloading === freebie.id}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
                        downloading === freebie.id
                          ? 'bg-gray-400 text-white cursor-wait'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {downloading === freebie.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Downloading...
                        </span>
                      ) : (
                        '⬇️ Download'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upgrade CTA for Basic Users */}
        {userTier === 'basic' && downloadCount >= 4 && (
          <div className="mt-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">
              {downloadCount >= 5 ? '🚫 Download Limit Reached' : '⚠️ Almost at Your Download Limit'}
            </h3>
            <p className="mb-4">
              {downloadCount >= 5
                ? "You've downloaded all 5 freebies available on the Basic plan."
                : `You have ${5 - downloadCount} download${5 - downloadCount === 1 ? '' : 's'} remaining.`}
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Upgrade to Premium for Unlimited Downloads
            </button>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewFreebie && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">{previewFreebie.title}</h2>
                  <p className="text-indigo-100 text-sm mt-1">
                    {previewFreebie.category.replace('_', ' ').toUpperCase()}
                  </p>
                </div>
                <button
                  onClick={() => setPreviewFreebie(null)}
                  className="text-white hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600">{previewFreebie.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 text-sm mb-1">File Type</h4>
                  <p className="text-gray-600">{previewFreebie.file_type?.toUpperCase() || 'FILE'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 text-sm mb-1">File Size</h4>
                  <p className="text-gray-600">{formatFileSize(previewFreebie.file_size)}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 text-sm mb-1">Required Tier</h4>
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${getTierBadgeColor(previewFreebie.required_tier)}`}>
                    {previewFreebie.required_tier.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 text-sm mb-1">Total Downloads</h4>
                  <p className="text-gray-600">{previewFreebie.download_count}</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> This is a preview. Click "Download Now" to get the full resource.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setPreviewFreebie(null)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDownload(previewFreebie.id, previewFreebie.title);
                  setPreviewFreebie(null);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                ⬇️ Download Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={helpGuides.freebieVault.title}
        steps={helpGuides.freebieVault.steps}
      />
    </div>
  );
};

export default Freebies;
